import React from 'react';
import { useField } from 'formik';
import { RadioGroup as ShadRadioGroup, RadioGroupItem } from '@dashboard/components/ui/radio-group';
import { cn } from '@dashboard/lib/utils';
import { OPTION_GROUP_CLASSNAME } from '@dashboard/components/Formik/components/optionGroupClass';

import Label from '@dashboard/components/Form/components/Label';

const findNameInChildren = children => {
  let found;
  const visit = node => {
    if (found) return;
    if (!React.isValidElement(node)) return;
    if (node.props && typeof node.props.name === 'string') {
      found = node.props.name;
      return;
    }
    if (node.props && node.props.children) {
      React.Children.forEach(node.props.children, visit);
    }
  };
  React.Children.forEach(children, visit);
  return found;
};

const RadioGroup = ({ name: nameProp, children, className }) => {
  const derivedName = nameProp || findNameInChildren(children) || '';
  const [field, , helpers] = useField(derivedName);

  return (
    <ShadRadioGroup
      value={field.value ?? ''}
      onValueChange={value => helpers.setValue(value)}
      className={cn(OPTION_GROUP_CLASSNAME, className)}
      aria-labelledby={derivedName}
    >
      {children}
    </ShadRadioGroup>
  );
};

const RadioWrapper = ({ children, hasHelpText }) => (
  <div className={`flex gap-x-1.5 ${hasHelpText ? 'items-start' : 'items-center'}`}>{children}</div>
);

const RadioLabel = ({ children, helpText, ...props }) => (
  <div>
    <Label isInline className="mb-0" {...props}>
      {children}
    </Label>
    {helpText && (
      <p className="mt-0.5 text-xs text-gray-500 dark:text-muted-foreground">{helpText}</p>
    )}
  </div>
);

const RadioInput = ({ value, name, id, disabled, hasHelpText, className, ...props }) => (
  <RadioGroupItem
    id={id}
    value={value}
    disabled={disabled}
    className={hasHelpText ? `mt-1 ${className ?? ''}` : className}
    {...props}
  />
);

const Radio = {
  Group: RadioGroup,
  Wrapper: RadioWrapper,
  Label: RadioLabel,
  Input: RadioInput,
};

export default Radio;
